// =============================================================================
// src/data/analytics-overlay-series.ts — Séries do chart a partir das linhas
// diárias de sl_serving_v_mentions_daily (agregação client-side, plano F3.7):
// days = últimos 10 dias com dado, weeks = últimas 4 semanas (segunda a
// domingo), months = últimos 4 meses civis. Sem sentimento/reach nesta fase:
// positive/neutral/negative/reach ficam 0 (a tab de sentimento é desabilitada
// quando live e a polyline de alcance some quando a série toda é 0).
// A âncora temporal é o MAIOR dia presente nos dados (determinístico, sem clock).
// =============================================================================

import type { AnalyticsMentionsDailyRow } from './analytics-live-query';
import type { OmarAnalyticsChartSample, OmarAnalyticsChartSeries } from '../features/analytics/analytics-types';
import {
  analyticsDayFullLabel,
  analyticsDayLabel,
  analyticsMonthFullLabel,
  analyticsMonthLabel,
  analyticsPlatformUiLabelFor,
  analyticsPtBrCompactNumber,
  analyticsPtBrInteger,
} from './analytics-overlay-format';

const ANALYTICS_DAYS_POINTS = 10;
const ANALYTICS_WEEKS_POINTS = 4;
const ANALYTICS_MONTHS_POINTS = 4;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Balde agregado por rótulo temporal (dia, semana ou mês). */
interface AnalyticsSeriesBucket {
  readonly bucketKey: string;
  mentions: number;
  interactions: number;
  readonly byPlatformLabel: Map<string, number>;
}

/** Séries days/weeks/months; undefined quando não há linha diária (mock fica). */
export function buildAnalyticsChartSeriesPatch(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
): readonly OmarAnalyticsChartSeries[] | undefined {
  if (dailyRows.length === 0) return undefined;
  return [
    { granularity: 'days', label: 'Dias', points: analyticsSeriesPoints(dailyRows, 'days') },
    { granularity: 'weeks', label: 'Semanas', points: analyticsSeriesPoints(dailyRows, 'weeks') },
    { granularity: 'months', label: 'Meses', points: analyticsSeriesPoints(dailyRows, 'months') },
  ];
}

/** Totais (menções + interações + views) numa janela de dias relativa ao último dia com dado. */
export function analyticsWindowTotals(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  windowDays: number,
  windowsBack: number,
): { readonly mentions: number; readonly interactions: number; readonly views: number } {
  const anchorDay = analyticsAnchorDay(dailyRows);
  if (!anchorDay) return { mentions: 0, interactions: 0, views: 0 };
  const windowEndMs = analyticsDayToMs(anchorDay) - windowsBack * windowDays * DAY_MS;
  const windowStartMs = windowEndMs - (windowDays - 1) * DAY_MS;
  let mentions = 0;
  let interactions = 0;
  let views = 0;
  for (const dailyRow of dailyRows) {
    const rowMs = analyticsDayToMs(dailyRow.day);
    if (rowMs < windowStartMs || rowMs > windowEndMs) continue;
    mentions += dailyRow.mentions;
    interactions += dailyRow.likes_total + dailyRow.comments_total + dailyRow.shares_total;
    // Views REAIS (TikTok/Reels) — substituem o KPI mock "Alcance estimado"
    // (2026-07-25, "remover os conteudos mockados").
    views += dailyRow.views_total;
  }
  return { mentions, interactions, views };
}

/** Linhas dentro da janela corrente de N dias (ancorada no último dia com dado). */
export function analyticsRowsInCurrentWindow(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  windowDays: number,
): readonly AnalyticsMentionsDailyRow[] {
  const anchorDay = analyticsAnchorDay(dailyRows);
  if (!anchorDay) return [];
  const windowEndMs = analyticsDayToMs(anchorDay);
  const windowStartMs = windowEndMs - (windowDays - 1) * DAY_MS;
  return dailyRows.filter((dailyRow) => {
    const rowMs = analyticsDayToMs(dailyRow.day);
    return rowMs >= windowStartMs && rowMs <= windowEndMs;
  });
}

/** Último dia (ISO) presente nas linhas — âncora das janelas e das séries. */
export function analyticsAnchorDay(dailyRows: readonly AnalyticsMentionsDailyRow[]): string | null {
  let anchorDay: string | null = null;
  for (const dailyRow of dailyRows) {
    if (anchorDay === null || dailyRow.day > anchorDay) anchorDay = dailyRow.day;
  }
  return anchorDay;
}

function analyticsSeriesPoints(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  granularity: 'days' | 'weeks' | 'months',
): readonly OmarAnalyticsChartSample[] {
  const buckets = analyticsBucketsFor(dailyRows, granularity);
  const pointsLimit = granularity === 'days' ? ANALYTICS_DAYS_POINTS : granularity === 'weeks' ? ANALYTICS_WEEKS_POINTS : ANALYTICS_MONTHS_POINTS;
  const sortedKeys = [...buckets.keys()].sort();
  return sortedKeys.slice(-pointsLimit).map((bucketKey, bucketIndex) => {
    /* v8 ignore next -- chave veio de buckets.keys(); ?? exigido pelo noUncheckedIndexedAccess */
    const bucket = buckets.get(bucketKey) ?? { bucketKey, mentions: 0, interactions: 0, byPlatformLabel: new Map<string, number>() };
    return analyticsChartSampleFromBucket(bucket, granularity, bucketIndex);
  });
}

function analyticsBucketsFor(
  dailyRows: readonly AnalyticsMentionsDailyRow[],
  granularity: 'days' | 'weeks' | 'months',
): Map<string, AnalyticsSeriesBucket> {
  const buckets = new Map<string, AnalyticsSeriesBucket>();
  for (const dailyRow of dailyRows) {
    const bucketKey = analyticsBucketKeyFor(dailyRow.day, granularity);
    const bucket = buckets.get(bucketKey) ?? { bucketKey, mentions: 0, interactions: 0, byPlatformLabel: new Map<string, number>() };
    bucket.mentions += dailyRow.mentions;
    bucket.interactions += dailyRow.likes_total + dailyRow.comments_total + dailyRow.shares_total;
    const platformLabel = analyticsPlatformUiLabelFor(dailyRow.platform, dailyRow.content_kind);
    bucket.byPlatformLabel.set(platformLabel, (bucket.byPlatformLabel.get(platformLabel) ?? 0) + dailyRow.mentions);
    buckets.set(bucketKey, bucket);
  }
  return buckets;
}

/** dia → chave do balde: o próprio dia, a segunda-feira da semana, ou 'YYYY-MM'. */
function analyticsBucketKeyFor(isoDay: string, granularity: 'days' | 'weeks' | 'months'): string {
  if (granularity === 'days') return isoDay;
  if (granularity === 'months') return isoDay.slice(0, 7);
  const dayMs = analyticsDayToMs(isoDay);
  const weekday = new Date(dayMs).getUTCDay();
  const mondayOffsetDays = (weekday + 6) % 7;
  return new Date(dayMs - mondayOffsetDays * DAY_MS).toISOString().slice(0, 10);
}

function analyticsChartSampleFromBucket(
  bucket: AnalyticsSeriesBucket,
  granularity: 'days' | 'weeks' | 'months',
  bucketIndex: number,
): OmarAnalyticsChartSample {
  const labels = analyticsBucketLabels(bucket.bucketKey, granularity, bucketIndex);
  const dominant = analyticsDominantPlatform(bucket);
  return {
    label: labels.label,
    fullLabel: labels.fullLabel,
    mentions: bucket.mentions,
    reach: 0,
    reachLabel: '—',
    positive: 0,
    neutral: 0,
    negative: 0,
    sourceLabel: dominant,
    sampleTitle: `${analyticsPtBrInteger(bucket.mentions)} mencoes no periodo`,
    sampleText: `${dominant} lidera o volume; interacoes somam ${analyticsPtBrCompactNumber(bucket.interactions)}.`,
    actionHint: 'Serie do monitoramento ao vivo (social_listening).',
  };
}

function analyticsBucketLabels(
  bucketKey: string,
  granularity: 'days' | 'weeks' | 'months',
  bucketIndex: number,
): { readonly label: string; readonly fullLabel: string } {
  if (granularity === 'days') return { label: analyticsDayLabel(bucketKey), fullLabel: analyticsDayFullLabel(bucketKey) };
  if (granularity === 'months') return { label: analyticsMonthLabel(bucketKey), fullLabel: analyticsMonthFullLabel(bucketKey) };
  return { label: `Sem ${bucketIndex + 1}`, fullLabel: `Semana de ${analyticsDayLabel(bucketKey)}` };
}

function analyticsDominantPlatform(bucket: AnalyticsSeriesBucket): string {
  let dominantLabel = 'Multicanal';
  let dominantMentions = 0;
  for (const [platformLabel, platformMentions] of bucket.byPlatformLabel) {
    if (platformMentions <= dominantMentions) continue;
    dominantLabel = platformLabel;
    dominantMentions = platformMentions;
  }
  return dominantLabel;
}

function analyticsDayToMs(isoDay: string): number {
  return Date.parse(`${isoDay}T00:00:00Z`);
}
